/**
 * Whether each remote a page has asked for is reachable, as signals.
 *
 * The heartbeat for every micro-frontend. A remote that fails to load is
 * watched until it answers - probed with backoff, and at once when the
 * browser comes back online, the tab becomes visible or the window gains
 * focus - and whatever was waiting for it follows its signal and loads it in.
 * A remote that is up is checked once a minute while the tab is visible, to
 * notice that it has gone. Nothing is probed for a remote nobody asked for.
 */
import { Signal } from '@crown/signal';
import { hasGlobal } from '../ir/guards';

export type RemoteState = 'up' | 'down' | 'unknown';

export interface RemoteTarget {
  readonly name: string;
  /** The remote's `mf-manifest.json` URL. */
  readonly entry: string;
  /** A health URL, when the remote serves one; checked as well as the manifest. */
  readonly health?: string;
}

export interface StatusEnvironment {
  probe(target: RemoteTarget): Promise<boolean>;
  /** Runs `fn` after `ms`; returns what cancels it. */
  setTimer(fn: () => void, ms: number): () => void;
  now(): number;
  visible(): boolean;
  /** Calls `fn` when the page comes back: online, visible or focused. Returns what stops it. */
  onWake(fn: () => void): () => void;
}

export const BACKOFF_MS = [2000, 4000, 8000, 16000, 30000] as const;
export const UP_INTERVAL_MS = 60_000;

/** Up when the manifest answers and parses, and the health URL, when there is one, answers. */
export async function defaultProbe(target: RemoteTarget): Promise<boolean> {
  try {
    const manifest = await fetch(target.entry, { cache: 'no-store' });
    if (!manifest.ok) return false;
    await manifest.json();
    if (!target.health) return true;
    const health = await fetch(target.health, { cache: 'no-store' });
    return health.ok;
  } catch {
    return false;
  }
}

function browserEnvironment(): StatusEnvironment {
  return {
    probe: defaultProbe,
    setTimer(fn, ms) {
      const id = setTimeout(fn, ms);
      return () => clearTimeout(id);
    },
    now: () => Date.now(),
    visible: () => !hasGlobal('document') || document.visibilityState !== 'hidden',
    onWake(fn) {
      if (!hasGlobal('window')) return () => {};
      const onVisible = () => {
        if (document.visibilityState !== 'hidden') fn();
      };
      window.addEventListener('online', fn);
      window.addEventListener('focus', fn);
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        window.removeEventListener('online', fn);
        window.removeEventListener('focus', fn);
        document.removeEventListener('visibilitychange', onVisible);
      };
    },
  };
}

interface Watched {
  target: RemoteTarget;
  attempt: number;
  lastSeen: number | null;
  cancel: (() => void) | null;
  checking: Promise<RemoteState> | null;
}

export interface RemoteStatusStore {
  /** The remote's state as a signal; `unknown` until it has been loaded or probed. */
  state(name: string): Signal.State<RemoteState>;
  /** Starts watching a remote the page uses, at the up-interval. */
  watch(target: RemoteTarget): void;
  /** A load failed: the remote is down, and is probed with backoff until it answers. */
  reportDown(target: RemoteTarget): void;
  /** A load succeeded: the remote is up. */
  reportUp(name: string): void;
  /** Probes at once, outside the schedule. */
  checkNow(name: string): Promise<RemoteState>;
  snapshot(): ReadonlyArray<{ name: string; entry: string; state: RemoteState; lastSeen: number | null }>;
  dispose(): void;
}

export function createRemoteStatus(overrides: Partial<StatusEnvironment> = {}): RemoteStatusStore {
  const env: StatusEnvironment = { ...browserEnvironment(), ...overrides };
  const states = new Map<string, Signal.State<RemoteState>>();
  const watched = new Map<string, Watched>();

  const state = (name: string): Signal.State<RemoteState> => {
    let signal = states.get(name);
    if (!signal) {
      signal = new Signal.State<RemoteState>('unknown');
      states.set(name, signal);
    }
    return signal;
  };

  const schedule = (entry: Watched): void => {
    entry.cancel?.();
    const down = state(entry.target.name).get() === 'down';
    const delay: number = down ? (BACKOFF_MS[Math.min(entry.attempt, BACKOFF_MS.length - 1)] ?? 30_000) : UP_INTERVAL_MS;
    entry.cancel = env.setTimer(() => {
      entry.cancel = null;
      // A hidden tab spends nothing; waking checks at once.
      if (!env.visible()) return;
      void check(entry).then(() => schedule(entry));
    }, delay);
  };

  const check = (entry: Watched): Promise<RemoteState> => {
    entry.checking ??= env
      .probe(entry.target)
      .then((ok) => {
        if (ok) {
          entry.attempt = 0;
          entry.lastSeen = env.now();
          state(entry.target.name).set('up');
          return 'up' as const;
        }
        entry.attempt++;
        state(entry.target.name).set('down');
        return 'down' as const;
      })
      .finally(() => {
        entry.checking = null;
      });
    return entry.checking;
  };

  const track = (target: RemoteTarget): Watched => {
    let entry = watched.get(target.name);
    if (!entry) {
      entry = { target, attempt: 0, lastSeen: null, cancel: null, checking: null };
      watched.set(target.name, entry);
    }
    return entry;
  };

  const stopWaking = env.onWake(() => {
    for (const entry of watched.values()) {
      if (state(entry.target.name).get() === 'down') void check(entry).then(() => schedule(entry));
      // A check that fell while the tab was hidden left the remote unscheduled; watch it again.
      else if (!entry.cancel) schedule(entry);
    }
  });

  return {
    state,
    watch(target) {
      const entry = track(target);
      if (!entry.cancel) schedule(entry);
    },
    reportDown(target) {
      const entry = track(target);
      const already = state(target.name).get() === 'down' && entry.cancel;
      state(target.name).set('down');
      if (!already) schedule(entry);
    },
    reportUp(name) {
      const entry = watched.get(name);
      state(name).set('up');
      if (entry) {
        entry.attempt = 0;
        entry.lastSeen = env.now();
        schedule(entry);
      }
    },
    async checkNow(name) {
      const entry = watched.get(name);
      if (!entry) return state(name).get();
      const result = await check(entry);
      schedule(entry);
      return result;
    },
    snapshot() {
      return [...watched.values()].map((entry) => ({
        name: entry.target.name,
        entry: entry.target.entry,
        state: state(entry.target.name).get(),
        lastSeen: entry.lastSeen,
      }));
    },
    dispose() {
      stopWaking();
      for (const entry of watched.values()) entry.cancel?.();
      watched.clear();
    },
  };
}
